import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null while loading, false when unauth
  const [loading, setLoading] = useState(true);

  // Session is carried by an httpOnly cookie — we can't read it from JS, so we
  // ask the backend who we are. If the cookie is missing/expired, /auth/me
  // returns 401 and we fall through to the unauthenticated state.
  useEffect(() => {
    // CRITICAL (Emergent Google Auth): if we're returning from the OAuth
    // callback, skip the /me check. AuthCallback exchanges the session_id and
    // establishes the session first — otherwise this fires before the cookie
    // exists and 401s.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  async function login(email, password) {
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data.user);
      return { ok: true, user: r.data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  async function register(payload) {
    try {
      const r = await api.post("/auth/register", payload);
      setUser(r.data.user);
      return { ok: true, user: r.data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  // Shopper email/password sign-up (role=customer).
  async function registerCustomer(payload) {
    try {
      const r = await api.post("/auth/customer/register", payload);
      setUser(r.data.user);
      return { ok: true, user: r.data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  // Exchange an Emergent Google-Auth session_id for our unified fm_token
  // session. Called once from AuthCallback.
  async function exchangeGoogle(sessionId) {
    try {
      const r = await api.post("/auth/google/session", { session_id: sessionId });
      setUser(r.data.user);
      return { ok: true, user: r.data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  // Request a password-reset link (always resolves ok — no email enumeration).
  async function forgotPassword(email) {
    try {
      const r = await api.post("/auth/forgot-password", { email });
      return { ok: true, data: r.data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  // Set a new password with a reset token.
  async function resetPassword(token, password) {
    try {
      const r = await api.post("/auth/reset-password", { token, password });
      setUser(false); // backend cleared the session cookie
      return { ok: true, data: r.data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  // Confirm an email address with a verification token.
  async function verifyEmail(token) {
    try {
      const r = await api.post("/auth/verify-email", { token });
      setUser((u) => (u ? { ...u, email_verified: true } : u));
      return { ok: true, data: r.data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  // Re-send the verification email to the signed-in user.
  async function resendVerification() {
    try {
      const r = await api.post("/auth/resend-verification");
      return { ok: true, data: r.data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Even if the network call fails, drop the client-side session so the
      // user is visibly logged out. The cookie clears on next successful call.
      console.warn("FloorMatch: logout request failed; clearing local session anyway.", e);
    }
    setUser(false);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, registerCustomer, exchangeGoogle, forgotPassword, resetPassword, verifyEmail, resendVerification, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
